import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  DatePicker,
  Form,
  Input,
  notification,
  Popconfirm,
  Space,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import DataGrid from 'components/DataGrid';
import { DataGridColDef, DataGridProps } from 'components/DataGrid/DataGrid';
import {
  dateToString,
  indexBy,
  setValueByNamePath,
  toDatePickerDate,
} from 'utils/utils2';
import { useFormList } from 'hooks/useFormList';
import ParListSelect from 'components/ParListSelect';
import DeleteButton from 'components/DeleteButton';

import {
  addAssetUsers,
  AssetUsersResponse,
  deleteAssetUsers,
  getAssetUsersListByAssetid,
  updateAssetUsers,
} from 'services/assets';
import { useQuery } from 'react-query';
import FormInput from 'components/FormInput';
import ThirdPartyInput from 'components/ThirdPartyInput';
import GetLabel from 'DisplayFramework/components/GetLabel';
import Button from 'components/Button';
import SaveButton from 'components/SaveButtonAction';
import { AssetUsersSelectedType } from 'components/AssetUsersSelect';

const NEW_USER_ID = -1;
export type AssetUsersProps = Pick<DataGridProps<any>, 'rowSelection'> & {
  id: number;
  showAsModal?: boolean;
  refetchTab?: boolean;
  onChange?: (Asset: AssetUsersSelectedType) => void;
  onSelect?: (Asset: AssetUsersSelectedType) => void;
  selectedRowKeys?: any;
};
type Values = {
  AssetUsersList: AssetUsersResponse[];
};

const AssetUsers = (props: AssetUsersProps) => {
  const { id, showAsModal, refetchTab, selectedRowKeys, onSelect } = props;
  const [form] = Form.useForm();
  const fieldName = 'AssetUsersList';
  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'assetrelid',
    addAsFirst: true,
  });

  const { data, refetch } = useQuery(
    ['getAssetUsersListByAssetid', { assetid: Number(id) }],
    () =>
      getAssetUsersListByAssetid({ assetid: Number(id) }).then(
        (res) => res.list || []
      ),
    { enabled: !!id }
  );

  const [selectedRowId, setSelectedRowId] = useState(null);

  useEffect(() => {
   refetch();
    if (data) {
      form.setFieldsValue({
        [fieldName]: data.map((rl: AssetUsersResponse) => ({
          ...rl,
          startdate: toDatePickerDate(rl.startdate),
          enddate: toDatePickerDate(rl.enddate),
          relvaluenew:
            rl.reltype === 'TP'
              ? {
                  tpid: rl.tpidtarget,
                  tpname: rl.tptargetname,
                }
              : rl.relvalue,
        })),
      });
    } 
  }, [form, data, refetchTab]);

  const onSaveOrEdit = useCallback(
    (record: any) => {
      const relation: AssetUsersResponse[] = form.getFieldValue(fieldName);
      const index = indexBy(relation, 'assetrelid')[record.assetrelid];
      const currentValue = {
        ...relation[index],
      };
      const assetrecord: {
        tpid: number;
        tpname: string;
      } = record.relvaluenew;

      const { assetrelid } = currentValue;
      assetrelid === NEW_USER_ID
        ? addAssetUsers({
            request: {
              ...currentValue,
              assetid: id,
              enddate: dateToString(currentValue.enddate),
              startdate: dateToString(currentValue.startdate),
              tpidtarget:
                currentValue.reltype === 'TP'
                  ? assetrecord.tpid
                  : (null as any),
              relvalue:
                currentValue.reltype === 'TP'
                  ? assetrecord.tpname
                  : record.relvaluenew,
            },
          }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message,
              });
              refetch();
            }
          })
        : updateAssetUsers({
            assetUserid: currentValue.assetrelid,

            request: {
              ...currentValue,
              assetid: id,
              tpidtarget:
                currentValue.reltype === 'TP'
                  ? assetrecord.tpid
                  : (null as any),
              relvalue:
                currentValue.reltype === 'TP'
                  ? assetrecord.tpname
                  : record.relvaluenew,
              enddate: dateToString(currentValue.enddate),
              startdate: dateToString(currentValue.startdate),
            },
          }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message,
              });
              refetch();
            }
          });
    },
    [form, refetch, id]
  );
  const getName = useCallback(
    (name: string) => (record: AssetUsersResponse) => {
      const functions = form.getFieldValue(fieldName);
      const index = indexBy(functions, 'assetrelid')[record.assetrelid];

      return [fieldName, index, name];
    },
    [form]
  );
  const columns = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        dataIndex: 'reltype',
        render: (_value, record, index) => (
          <FormInput noStyle name={getName('reltype')(record)}>
            <ParListSelect
              parcode="ASUSERTYPE"
              onSelect={(e) => {
                setValueByNamePath(form, [fieldName, index, 'relcode'], '');
                if ((e === 'TAG' || e === 'FREE') && record.reltype === 'TP') {
                  setValueByNamePath(
                    form,
                    [fieldName, index, 'relvaluenew'],
                    ''
                  );
                } else {
                  if (e === 'TP' && record.reltype === 'TP') {
                    setValueByNamePath(
                      form,
                      [fieldName, index, 'relvaluenew'],
                      {
                        tpid: record.relvaluenew.tpid,
                        tpname: record.relvaluenew.tpname,
                      }
                    );
                  } else if (
                    (e === 'TAG' || e === 'FREE') &&
                    !(record.reltype === 'TP')
                  ) {
                    setValueByNamePath(
                      form,
                      [fieldName, index, 'relvaluenew'],
                      record.relvaluenew
                    );
                  }
                }
              }}
            />
          </FormInput>
        ),
      },

      {
        dataIndex: 'relcode',
        render: (_value, record, index) => (
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => (
              <FormInput
                name={getName('relcode')(record)}
                rules={[{ required: true }]}
                noStyle
              >
                <ParListSelect
                  parcode="ASUSERCODE"
                  parvalueparent={getFieldValue(getName('reltype')(record))}
                />
              </FormInput>
            )}
          </Form.Item>
        ),
      },
      {
        dataIndex: 'relvaluenew',
        render: (_value, record, index) => (
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => {
              const reltype = getFieldValue(getName('reltype')(record));
              var reltypeValue;
              if (reltype === 'TP') {
                reltypeValue = <ThirdPartyInput />;
              }
              if (reltype === 'TAG') {
                reltypeValue = (
                  <ParListSelect
                    parcode={getFieldValue(getName('relcode')(record))}
                  />
                );
              }
              if (reltype === 'FREE') {
                reltypeValue = <Input />;
              }

              return (
                <FormInput
                  name={getName('relvaluenew')(record)}
                  rules={[{ required: true }]}
                  noStyle
                >
                  {reltypeValue}
                </FormInput>
              );
            }}
          </Form.Item>
        ),
      },
      {
        dataIndex: 'startdate',
        render: (_value, record, index) => (
          <Form.Item 
            name={getName('startdate')(record)} 
            noStyle 
            rules={[{ required: true, message: 'Please select a start date' }]}
          >
            <DatePicker/>
          </Form.Item>
        ),
      },
      {
        dataIndex: 'enddate',
        render: (_value, record, index) => (
          <Form.Item 
            name={getName('enddate')(record)} 
            noStyle 
            rules={[{ required: true, message: 'Please select an end date' }]}
          >
            <DatePicker />
          </Form.Item>
        ),
      },
      {
        key: 'actions',
        render: (_, _record) =>
          _record.assetrelid === NEW_USER_ID ? (
            <Space align="center">
              <SaveButton
                onClick={(ev) => {
                  ev.preventDefault();
                  onSaveOrEdit(_record);
                } } comcode="save"   
                size="small"             
              />
              <DeleteButton
                onlyIcon
                onClick={() => remove(_record.assetrelid)}
              />
            </Space>
          ) : !selectedRowKeys&&(
            <>
              <SaveButton
                onClick={(ev) => {
                  ev.preventDefault();
                  onSaveOrEdit(_record);
                } } comcode="update" 
                size="small"               
              />
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => {
                  deleteAssetUsers({
                    assetUserid: _record.assetrelid,
                  }).then((res) => {
                    notification.success({ message: res.message });
                    !selectedRowKeys && refetch();
                  });
                }}
              >
                <DeleteButton
                  onlyIcon
                  style={{ height: '40px', width: '40px' }}
                />
              </Popconfirm>
            </>
          ),
        width: 200,
        fixed: 'right',
      },
    ],

    [form, getName, onSaveOrEdit,refetch, remove]
  );

  const addRow = () => {
    add(row);
  };
  const rowSelection = {
    onChange: (selectedRowKey: any, selectedRow: any) => {
      setSelectedRowId(selectedRow.assetrelid);

      let AssetUser = [];
      for (const i in selectedRow) {
        AssetUser.push(selectedRow[i].assetrelid);
      }

      selectedRow.forEach((row: any, index: number) => {
        onSelect &&
          onSelect({
            ...row,
            assetid: id,
            assetrelid: row.assetrelid,
            relvalue: row.relvalue,
          });

      });
      form.setFieldsValue({
        assetid: id,
        assetrelid: row.assetrelid,
        relvalue: row.relvalue,
        relcode: row?.relcode,
      });
    },
  };
  return (
    <Card
      title={
        <GetLabel
          label="AssetUsers"
          style={{ fontWeight: 'bold', paddingLeft: 15 }}
        />
      }
    >
      <Form form={form} initialValues={row}>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const Relation: any[] = getFieldValue(fieldName);
            const isNewItemInTable =
              Relation?.find(({ assetrelid }) => assetrelid === NEW_USER_ID) !==
              undefined;
            return (
              <DataGrid
                rowKey={(record)=>record.assetrelid}
                dataSource={Relation}
                columns={
                  !showAsModal
                    ? columns
                    : columns.filter((cl) => cl.key !== 'actions')
                }
                rowSelection={
                  selectedRowKeys && {
                    type: 'radio',
                    ...rowSelection,
                  }
                }
                hideFilters
                extraActions={
                  !showAsModal && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={addRow}
                      disabled={isNewItemInTable}
                      comcode="add"
                    />
                  )
                }
              />
            );
          }}
        </Form.Item>
      </Form>
    </Card>
  );
};

const row: AssetUsersResponse = {
  tpidtarget: '' as any,
  assetrelid: NEW_USER_ID,
  enddate: '',
  relcode: '',
  reltype: '',
  relvalue: '',
  startdate: '',
  creationdate: '',
  assetid: 0,
  updatedate: '',
  useridcreate: 0,
  useridupdate: 0,
  tptargetname: '',
  assetname: '',
};

export default AssetUsers;
